import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useAutosave } from "@/hooks/useAutosave";
import api, { apiErr, isPaywall } from "@/lib/api";
import { MicButton } from "@/components/MicButton";
import { Container, PageHeader, PaywallNotice, GenerateButton } from "@/components/shared";
import RemainingCounter from "@/components/RemainingCounter";
import { toast } from "sonner";
import {
  LifeBuoy, Loader2, ShieldCheck, Lock, AlertTriangle, HelpCircle, Repeat, Heart,
  ArrowLeft, Plus, Trash2, Phone, Globe, Navigation, MapPin, Users, Scale, Brain, Instagram, Facebook,
  ChevronDown, ChevronUp, ListChecks,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const photoUrl = (p) => (p && p.startsWith("http") ? p : `${BACKEND}/api/afromap/photo/${p}`);

const ROUTE_FOR = { respond: "/reply", rights: "/rights", letter: "/courriers" };

export default function Situation() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isChild = user?.profile === "child";
  const [narrative, setNarrative] = useState("");
  useAutosave("situation", narrative, setNarrative);
  const [tried, setTried] = useState("");
  const [showTried, setShowTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [paywall, setPaywall] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [tab, setTab] = useState("main");
  const [usageTrigger, setUsageTrigger] = useState(0);

  const analyze = async () => {
    if (narrative.trim().length < 10) return toast.error("Raconte un peu plus ce que tu vis.");
    setBusy(true); setPaywall(false);
    try {
      const { data } = await api.post("/ai/situation", { narrative: narrative.trim(), already_tried: tried.trim() });
      setAnalysis(data);
      setTab("main");
      setUsageTrigger((t) => t + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      if (isPaywall(e)) setPaywall(true);
      else toast.error(apiErr(e));
    } finally { setBusy(false); }
  };

  const onStep = (key) => {
    if (ROUTE_FOR[key] === "/reply") return navigate(`/reply?comment=${encodeURIComponent(narrative)}`);
    if (ROUTE_FOR[key]) return navigate(ROUTE_FOR[key]);
    if (key === "journal") return setTab("journal");
    if (key === "talk") return setTab("talk");
    if (key === "steps") { setShowTried(true); document.getElementById("situation-tried")?.scrollIntoView({ behavior: "smooth" }); return; }
    if (key === "trusted_adult") return document.getElementById("situation-minor")?.scrollIntoView({ behavior: "smooth" });
    if (key === "understand") return document.getElementById("situation-analysis")?.scrollIntoView({ behavior: "smooth" });
  };

  const reset = () => { setAnalysis(null); setTab("main"); setPaywall(false); };

  return (
    <Container>
      <PageHeader eyebrow="LISA · Accompagnement"
        title="Je vis une situation"
        subtitle="Raconte librement ce que tu vis. On va comprendre ensemble, puis voir ce que tu peux faire — tu n'as pas à rester seul·e." />

      <RemainingCounter trigger={usageTrigger} kind="situation" />

      {!analysis && (
        <div className="max-w-2xl">
          <div className="card-surface p-3 mb-4 flex items-start gap-2 text-white/55 text-xs" data-testid="situation-privacy">
            <Lock size={15} className="text-gold shrink-0 mt-0.5" />
            <span>Ce que tu écris ici reste confidentiel et n'est <b>jamais</b> transmis automatiquement à qui que ce soit. Tu gardes le contrôle.</span>
          </div>

          <textarea data-testid="situation-input" value={narrative} onChange={(e) => setNarrative(e.target.value)}
            rows={7} placeholder={isChild ? "Ex. : À l'école, un élève se moque souvent de mes cheveux devant les autres. Je lui ai demandé d'arrêter mais il recommence." : "Ex. : Au travail, mon responsable fait souvent des remarques sur mon prénom et mes cheveux devant les collègues. Quand je dis que ça me dérange, on me dit que j'exagère. Ça revient chaque semaine…"}
            className="w-full bg-white/[0.04] border border-white/10 rounded-2xl px-4 py-3 outline-none text-sm focus:border-gold/40 transition-colors resize-none" />

          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <MicButton testid="situation-input-mic" value={narrative} onChange={setNarrative} />
            <button onClick={() => setShowTried((v) => !v)} className="text-xs text-white/50 hover:text-gold inline-flex items-center gap-1.5">
              <Repeat size={13} /> {showTried ? "Masquer" : "As-tu déjà tenté quelque chose ? (facultatif)"}
            </button>
          </div>
          {showTried && (
            <>
              <textarea id="situation-tried" data-testid="situation-tried" value={tried} onChange={(e) => setTried(e.target.value)}
                rows={3} placeholder={isChild ? "Ex. : J'en ai parlé à la maîtresse, mais ça continue…" : "Ex. : J'ai prévenu mon responsable, mais rien n'a changé…"}
                className="w-full mt-2 bg-white/[0.04] border border-white/10 rounded-2xl px-4 py-3 outline-none text-sm focus:border-gold/40 transition-colors resize-none" />
              <div className="mt-2"><MicButton testid="situation-tried-mic" value={tried} onChange={setTried} /></div>
            </>
          )}

          {paywall ? <div className="mt-4"><PaywallNotice /></div> : (
            <GenerateButton data-testid="situation-analyze-btn" onClick={analyze} disabled={busy} className="mt-4">
              {busy ? <><Loader2 size={16} className="animate-spin" /> LISA réfléchit…</> : <><LifeBuoy size={16} /> Comprendre ma situation</>}
            </GenerateButton>
          )}
        </div>
      )}

      {analysis && (
        <div className="max-w-2xl">
          <button onClick={reset} data-testid="situation-back" className="text-white/50 hover:text-white text-sm inline-flex items-center gap-1.5 mb-4">
            <ArrowLeft size={15} /> Raconter une autre situation
          </button>

          {/* Onglets */}
          <div className="flex gap-2 mb-5 flex-wrap">
            {[["main", "Ma situation"], ["journal", "Journal des faits"], ["talk", "Parler à quelqu'un"]].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`situation-tab-${k}`}
                className={`text-sm px-4 py-1.5 rounded-full border transition-colors ${tab === k ? "gold-gradient text-black border-transparent" : "border-white/15 text-white/60 hover:text-white"}`}>{l}</button>
            ))}
          </div>

          {tab === "main" && <MainAnalysis a={analysis} onStep={onStep} />}
          {tab === "journal" && <Journal />}
          {tab === "talk" && <TalkToSomeone />}
        </div>
      )}
    </Container>
  );
}

function Bullets({ items, icon: Icon, tone }) {
  if (!items || items.length === 0) return null;
  const c = { warn: "text-amber-300", amb: "text-white/60", info: "text-sky-300" }[tone] || "text-white/60";
  return (
    <ul className="space-y-1.5">
      {items.map((t, i) => (
        <li key={i} className="flex items-start gap-2 text-sm text-white/80">
          <Icon size={15} className={`${c} shrink-0 mt-0.5`} /> <span>{t}</span>
        </li>
      ))}
    </ul>
  );
}

function MainAnalysis({ a, onStep }) {
  const [showFull, setShowFull] = useState(false);
  const [showSteps, setShowSteps] = useState(false);
  const isMinor = !!a.is_minor;

  // Choix simples prioritaires — réduisent la surcharge cognitive.
  const primary = isMinor
    ? [
        { key: "trusted_adult", icon: Users, label: "Parler à un adulte de confiance" },
        { key: "understand", icon: HelpCircle, label: "Comprendre ce qui se passe" },
        { key: "talk", icon: Heart, label: "Trouver de l'aide" },
      ]
    : [
        { key: "__steps", icon: ListChecks, label: "Que faire maintenant" },
        { key: "rights", icon: Scale, label: "Comprendre mes droits" },
        { key: "talk", icon: Users, label: "Parler à quelqu'un" },
      ];

  const onPrimary = (key) => {
    if (key === "__steps") return setShowSteps((v) => !v);
    if (key === "understand") return setShowFull(true);
    onStep(key);
  };

  const hasDetail = a.reading || a.concerning_elements?.length || a.pattern ||
    a.ambiguous_elements?.length || a.need_more_info?.length || (a.recourse?.length && !a.discouraged);

  return (
    <div id="situation-analysis" className="space-y-4" data-testid="situation-result">
      {/* Résumé court et rassurant */}
      {(a.acknowledgement || a.reassurance) && (
        <div className="card-surface p-4 border-l-2 border-gold" data-testid="situation-summary">
          {a.acknowledgement && <p className="text-white/90">{a.acknowledgement}</p>}
          {a.reassurance && <p className="text-gold/90 text-sm mt-2">{a.reassurance}</p>}
        </div>
      )}

      {/* Sécurité — toujours visible */}
      {a.discouraged && (
        <div className="card-surface p-4 border-l-2 border-rose-400/60" data-testid="situation-discouraged">
          <p className="text-rose-200 text-sm flex items-start gap-2"><Heart size={15} className="shrink-0 mt-0.5" />
            {(a.recourse && a.recourse[0]) || "Ce n'est pas de ta faute, et tu n'as pas à porter ça seul·e."}</p>
        </div>
      )}

      {a.minor_safety && (
        <div id="situation-minor" className="card-surface p-4 border-l-2 border-emerald-400/60" data-testid="situation-minor">
          <p className="text-emerald-200 font-medium">{a.minor_safety.message}</p>
          <div className="flex flex-wrap gap-2 mt-3">
            {a.minor_safety.trusted_adults.map((t, i) => (
              <span key={i} className="text-xs rounded-full border border-emerald-400/30 text-emerald-100/90 px-3 py-1">{t}</span>
            ))}
          </div>
        </div>
      )}

      {/* Choix simples prioritaires */}
      <div className="grid gap-2.5" data-testid="situation-primary-choices">
        {primary.map((c) => (
          <button key={c.key} onClick={() => onPrimary(c.key)} data-testid={`situation-primary-${c.key.replace("__", "")}`}
            className="card-surface p-4 text-left flex items-center gap-3 hover:border-gold/40 transition-colors">
            <span className="h-9 w-9 rounded-full bg-gold/10 flex items-center justify-center text-gold shrink-0"><c.icon size={17} /></span>
            <span className="text-sm text-white/90 font-medium flex-1">{c.label}</span>
            {c.key === "__steps" && (showSteps ? <ChevronUp size={16} className="text-white/40" /> : <ChevronDown size={16} className="text-white/40" />)}
          </button>
        ))}
      </div>

      {/* Plus d'options concrètes (dépliable) */}
      {showSteps && (
        <div className="grid grid-cols-2 gap-3" data-testid="situation-next-steps">
          {a.next_steps.map((s) => (
            <button key={s.key} onClick={() => onStep(s.key)} data-testid={`situation-step-${s.key}`}
              className="card-surface p-4 text-left hover:border-gold/40 transition-colors flex flex-col gap-2 min-h-[92px]">
              <span className="text-2xl">{s.emoji}</span>
              <span className="text-sm text-white/85 leading-tight">{s.label}</span>
            </button>
          ))}
        </div>
      )}

      {/* Analyse complète — seulement à la demande */}
      {hasDetail && (
        <button onClick={() => setShowFull((v) => !v)} data-testid="situation-toggle-full"
          className="text-sm text-white/60 hover:text-gold inline-flex items-center gap-1.5 pt-1">
          {showFull ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
          {showFull ? "Masquer l'analyse complète" : "Voir l'analyse complète"}
        </button>
      )}

      {showFull && (
        <div className="space-y-4" data-testid="situation-full-analysis">
          {a.reading && (
            <div className="card-surface p-4">
              <p className="text-white/70 text-sm">{a.reading}</p>
            </div>
          )}

          {a.concerning_elements?.length > 0 && (
            <div className="card-surface p-4">
              <h4 className="text-sm font-medium mb-2 text-amber-200">Plusieurs éléments méritent d'être pris au sérieux</h4>
              <Bullets items={a.concerning_elements} icon={AlertTriangle} tone="warn" />
            </div>
          )}

          {a.pattern && (
            <div className="card-surface p-4">
              <p className="text-sm text-white/80 flex items-start gap-2"><Repeat size={15} className="text-gold shrink-0 mt-0.5" /> {a.pattern}</p>
            </div>
          )}

          {a.ambiguous_elements?.length > 0 && (
            <div className="card-surface p-4">
              <h4 className="text-sm font-medium mb-2 text-white/70">Éléments ambigus (plusieurs explications possibles)</h4>
              <Bullets items={a.ambiguous_elements} icon={HelpCircle} tone="amb" />
            </div>
          )}

          {a.need_more_info?.length > 0 && (
            <div className="card-surface p-4">
              <h4 className="text-sm font-medium mb-2 text-sky-200">Ce qui aiderait à y voir plus clair</h4>
              <Bullets items={a.need_more_info} icon={HelpCircle} tone="info" />
            </div>
          )}

          {a.recourse?.length > 0 && !a.discouraged && (
            <div className="card-surface p-4">
              <h4 className="text-sm font-medium mb-2 text-white/70">Et si une démarche a déjà échoué</h4>
              <Bullets items={a.recourse} icon={Navigation} tone="info" />
            </div>
          )}
        </div>
      )}

      <p className="text-white/35 text-xs px-1 pt-2 flex items-start gap-2">
        <ShieldCheck size={13} className="shrink-0 mt-0.5" /> {a.disclaimer}
      </p>
    </div>
  );
}

// ------------------------------------------------------------------ Journal des faits
const EMPTY_ENTRY = { date: "", place: "", description: "", people: "", witnesses: "", action_taken: "", response: "" };

function Journal() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState(EMPTY_ENTRY);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/situation/journal"); setEntries(data.entries || []); }
    catch (e) { toast.error(apiErr(e)); } finally { setLoaded(true); }
  }, []);
  if (!loaded) load();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const add = async () => {
    if (!form.description.trim()) return toast.error("Décris au moins ce qui s'est passé.");
    setBusy(true);
    try {
      await api.post("/situation/journal", form);
      toast.success("Fait enregistré.");
      setForm(EMPTY_ENTRY); setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/situation/journal/${id}`); setEntries((prev) => prev.filter((x) => x.entry_id !== id)); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const inp = "w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 outline-none text-sm focus:border-gold/40";

  return (
    <div className="space-y-4" data-testid="situation-journal">
      <div className="card-surface p-3 flex items-start gap-2 text-white/55 text-xs">
        <ShieldCheck size={15} className="text-gold shrink-0 mt-0.5" />
        <span>Garde une trace chronologique des faits. Ce journal t'aide à organiser la situation — ce n'est pas une preuve juridique garantie, mais il peut servir à préparer un courrier ou résumer ce que tu vis. Il reste privé et supprimable.</span>
      </div>

      {!open && (
        <button onClick={() => setOpen(true)} data-testid="journal-add-btn"
          className="gold-gradient text-black rounded-full px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2">
          <Plus size={16} /> Ajouter un fait
        </button>
      )}

      {open && (
        <div className="card-surface p-4 space-y-2.5" data-testid="journal-form">
          <div className="grid grid-cols-2 gap-2.5">
            <input type="date" value={form.date} onChange={set("date")} className={inp} data-testid="journal-date" />
            <input placeholder={isChild ? "Lieu (classe, récré, cantine…)" : "Lieu (bureau, école…)"} value={form.place} onChange={set("place")} className={inp} data-testid="journal-place" />
          </div>
          <textarea placeholder="Que s'est-il passé ? (paroles, gestes, contexte)" rows={3} value={form.description} onChange={set("description")} className={`${inp} resize-none`} data-testid="journal-description" />
          <div><MicButton testid="journal-description-mic" value={form.description} onChange={(v) => setForm((f) => ({ ...f, description: v }))} /></div>
          <div className="grid grid-cols-2 gap-2.5">
            <input placeholder="Personnes impliquées" value={form.people} onChange={set("people")} className={inp} data-testid="journal-people" />
            <input placeholder="Témoins" value={form.witnesses} onChange={set("witnesses")} className={inp} data-testid="journal-witnesses" />
          </div>
          <input placeholder="Démarche effectuée (à qui en as-tu parlé ?)" value={form.action_taken} onChange={set("action_taken")} className={inp} data-testid="journal-action" />
          <input placeholder="Réponse obtenue" value={form.response} onChange={set("response")} className={inp} data-testid="journal-response" />
          <div className="flex gap-2 pt-1">
            <button onClick={add} disabled={busy} data-testid="journal-save"
              className="gold-gradient text-black rounded-full px-5 py-2 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Enregistrer
            </button>
            <button onClick={() => { setOpen(false); setForm(EMPTY_ENTRY); }} className="text-white/50 hover:text-white text-sm px-3">Annuler</button>
          </div>
        </div>
      )}

      {entries.length === 0 && loaded && !open && (
        <p className="text-white/40 text-sm">Aucun fait enregistré pour l'instant.</p>
      )}

      <div className="space-y-2.5">
        {entries.map((e) => (
          <div key={e.entry_id} className="card-surface p-4" data-testid={`journal-entry-${e.entry_id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="text-xs text-gold/80">{e.date}{e.place ? ` · ${e.place}` : ""}</div>
              <button onClick={() => del(e.entry_id)} data-testid={`journal-del-${e.entry_id}`} className="text-white/40 hover:text-rose-400"><Trash2 size={14} /></button>
            </div>
            <p className="text-sm text-white/85 mt-1">{e.description}</p>
            <div className="text-xs text-white/45 mt-2 space-y-0.5">
              {e.people && <p>Personnes : {e.people}</p>}
              {e.witnesses && <p>Témoins : {e.witnesses}</p>}
              {e.action_taken && <p>Démarche : {e.action_taken}</p>}
              {e.response && <p>Réponse : {e.response}</p>}
            </div>
          </div>
        ))}
      </div>

      {entries.length > 0 && (
        <button onClick={() => navigate("/courriers")} data-testid="journal-to-letter"
          className="text-sm text-gold hover:underline inline-flex items-center gap-1.5">
          <Scale size={14} /> Utiliser ces faits pour préparer un courrier
        </button>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ Parler à quelqu'un
const AXES = [
  { key: "psy", emoji: "🧠", label: "Écoute / accompagnement psychologique", icon: Brain },
  { key: "legal", emoji: "⚖️", label: "Juridique / droits", icon: Scale },
  { key: "assoc", emoji: "🤝", label: "Associations / accompagnement", icon: Users },
];

function TalkToSomeone() {
  const navigate = useNavigate();
  const [mode, setMode] = useState(null);   // "local" | "remote"
  const [axis, setAxis] = useState(null);
  const [city, setCity] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async (ax, c, m) => {
    setBusy(true); setAxis(ax);
    const remote = m === "remote";
    const q = `axis=${ax}&remote=${remote}${!remote && c ? `&city=${encodeURIComponent(c)}` : ""}`;
    try { const { data } = await api.get(`/situation/professionals?${q}`); setData(data); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const pickMode = (m) => { setMode(m); setAxis(null); setData(null); };

  return (
    <div className="space-y-4" data-testid="situation-talk">
      <div className="card-surface p-3 flex items-start gap-2 text-white/55 text-xs">
        <Lock size={15} className="text-gold shrink-0 mt-0.5" />
        <span>BIZARRE AI facilite seulement la mise en relation. Aucune information (ta conversation, ton récit, tes documents) n'est transmise automatiquement — tu contactes toi-même la personne si tu le souhaites.</span>
      </div>

      {/* Mode : près de chez moi / à distance */}
      <div className="grid grid-cols-2 gap-3" data-testid="talk-modes">
        <button onClick={() => pickMode("local")} data-testid="talk-mode-local"
          className={`card-surface p-4 text-left flex flex-col gap-1.5 transition-colors ${mode === "local" ? "border-gold/50" : "hover:border-gold/30"}`}>
          <span className="text-xl">📍</span>
          <span className="text-sm text-white/90 font-medium">Près de chez moi</span>
          <span className="text-white/45 text-xs">En présentiel, dans ta ville</span>
        </button>
        <button onClick={() => pickMode("remote")} data-testid="talk-mode-remote"
          className={`card-surface p-4 text-left flex flex-col gap-1.5 transition-colors ${mode === "remote" ? "border-sky-400/50" : "hover:border-gold/30"}`}>
          <span className="text-xl">💻</span>
          <span className="text-sm text-white/90 font-medium">À distance / visio</span>
          <span className="text-white/45 text-xs">Partout, où que tu sois</span>
        </button>
      </div>

      {mode && (
        <>
          <div className="grid gap-3">
            {AXES.map((ax) => (
              <button key={ax.key} onClick={() => load(ax.key, city, mode)} data-testid={`talk-axis-${ax.key}`}
                className={`card-surface p-4 text-left flex items-center gap-3 transition-colors ${axis === ax.key ? "border-gold/50" : "hover:border-gold/30"}`}>
                <span className="text-2xl">{ax.emoji}</span>
                <span className="text-sm text-white/85">{ax.label}</span>
              </button>
            ))}
          </div>

          {mode === "local" && axis && (
            <div className="flex gap-2">
              <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Ville (facultatif)"
                className="flex-1 bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 outline-none text-sm focus:border-gold/40" data-testid="talk-city" />
              <button onClick={() => load(axis, city, mode)} className="gold-gradient text-black rounded-xl px-4 text-sm font-medium">Filtrer</button>
            </div>
          )}
        </>
      )}

      {busy && <p className="text-white/40 text-sm flex items-center gap-2"><Loader2 size={15} className="animate-spin" /> Recherche…</p>}

      {data && !busy && (
        <div className="space-y-3" data-testid="talk-results">
          {data.professionals.length === 0 ? (
            <div className="card-surface p-4 text-sm text-white/60">
              {mode === "remote"
                ? "Aucun professionnel disponible à distance n'est encore référencé pour ce besoin."
                : `Aucun professionnel validé n'est encore référencé pour ce besoin${city ? ` à ${city}` : ""}.`}
              <button onClick={() => navigate("/carte-afro")} className="block mt-2 text-gold hover:underline inline-flex items-center gap-1.5">
                <MapPin size={14} /> Explorer la Carte Afro
              </button>
            </div>
          ) : data.professionals.map((p) => (
            <div key={p.place_id} className="card-surface p-4" data-testid={`talk-pro-${p.place_id}`}>
              <div className="flex gap-3">
                {(p.logo || p.photos?.[0])
                  ? <img src={photoUrl(p.logo || p.photos[0])} alt="" className="h-14 w-14 rounded-xl object-cover shrink-0" />
                  : <div className="h-14 w-14 rounded-xl bg-white/[0.05] flex items-center justify-center text-gold/40 shrink-0"><Users size={20} /></div>}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-medium">{p.name}</span>
                    {p.verified && <span className="text-[10px] text-emerald-300 border border-emerald-400/30 rounded-full px-1.5">✓ Vérifié</span>}
                    {p.partner && <span className="text-[10px] text-gold border border-gold/40 rounded-full px-1.5">★ Partenaire</span>}
                    {p.remote && <span className="text-[10px] text-sky-300 border border-sky-400/30 rounded-full px-1.5">💻 À distance</span>}
                  </div>
                  <p className="text-white/45 text-xs mt-0.5">{[p.remote ? "À distance / visio" : p.city, p.category].filter(Boolean).join(" · ")}</p>
                  {p.remote && <p className="text-sky-300/80 text-xs mt-0.5 flex items-center gap-1"><Globe size={12} /> Reçoit en visioconférence</p>}
                  {p.description && <p className="text-white/60 text-sm mt-1 line-clamp-2">{p.description}</p>}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {p.phone && <a href={`tel:${p.phone}`} className="talk-btn"><Phone size={13} /> Appeler</a>}
                {p.website && <a href={p.website} target="_blank" rel="noreferrer" className="talk-btn"><Globe size={13} /> Site</a>}
                {p.socials?.instagram && <a href={p.socials.instagram} target="_blank" rel="noreferrer" className="talk-btn"><Instagram size={13} /></a>}
                {p.socials?.facebook && <a href={p.socials.facebook} target="_blank" rel="noreferrer" className="talk-btn"><Facebook size={13} /></a>}
              </div>
              <p className="text-white/35 text-[11px] mt-3 pt-2 border-t border-white/5">{data.disclaimer}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
